import React, { useMemo } from 'react';
import { isNil } from 'lodash';
import { FormLayout } from 'modules/campaigns/components';
import { FORM_MODES } from 'modules/campaigns/config';
import { useFormFieldValue, withFormDataProvider } from 'modules/taboola-common-frontend-modules/formData';
import { FormInitBase } from 'modules/taboola-common-frontend-modules/forms';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { SaveAsDraftLabel } from '../components/SaveAsDraftLabel';
import { ChatStep } from '../components/WizardSteps/Chat/ChatStep';
import { chatSender, INITIAL_CHAT_STATE } from '../consts/chatState';
import { LANDING_PAGE_STATUS } from '../consts/landingPageStatus';
import { useLandingPageEditorState } from '../hooks/useLandingPageEditorState';
import styles from './landingPageEditor.module.scss';

const LandingPageEditor = ({ onCancel }) => {
    const { submit, submitStep } = useLandingPageEditorState();
    const { value: chatHistory = INITIAL_CHAT_STATE } = useFormFieldValue({ field: 'chatHistory' });
    const { value: status } = useFormFieldValue({ field: 'status' });
    const { value: navigationTimestamp } = useFormFieldValue({ field: 'navigationTimestamp' });

    // Check if there are any user messages in chat history
    const hasChatInteraction = useMemo(
        () => chatHistory.some(message => message.sender === chatSender.user),
        [chatHistory]
    );

    // Check if user navigated through history
    const hasNavigated = !isNil(navigationTimestamp);

    // Determine cancel button label for edit mode:
    // History navigation + DRAFT → Always "Save as draft"
    // History navigation + PUBLISHED → default "Cancel" initially, "Save as draft" after chat interaction
    // No history navigation → default "Cancel" initially, "Save as draft" after chat interaction
    const chatCancelLabel = useMemo(() => {
        if (hasNavigated && status === LANDING_PAGE_STATUS.DRAFT) {
            // History navigation with DRAFT status → always "Save as draft"
            return <SaveAsDraftLabel />;
        }

        if (hasNavigated && status === LANDING_PAGE_STATUS.PUBLISHED) {
            // History navigation with PUBLISHED status → default "Cancel" or "Save as draft" based on chat interaction
            return hasChatInteraction ? <SaveAsDraftLabel /> : undefined;
        }

        // No history navigation → based on chat interaction only
        return hasChatInteraction ? <SaveAsDraftLabel /> : undefined;
    }, [hasNavigated, status, hasChatInteraction]);

    return (
        <FormInitBase>
            <FormLayout hideFooter={true} contentContainerClassName={styles['container']}>
                <div className={styles['header-wrapper']}>
                    <div className={styles['header-container']}>
                        <h2 className={styles['header']}>
                            <FormattedMessage
                                id="landing.pages.form.editor.header"
                                defaultMessage="Preview your advertorial"
                            />
                        </h2>
                    </div>
                </div>
                <ChatStep
                    className={styles['chat-wrapper']}
                    containerClassName={styles['chat-container-edit-mode']}
                    mode={FORM_MODES.EDIT}
                    onCancel={onCancel}
                    onSubmit={submit}
                    onChatSubmit={submitStep}
                    cancelLabel={chatCancelLabel}
                />
            </FormLayout>
        </FormInitBase>
    );
};

const LandingPageEditorWithFormDataProvider = withFormDataProvider(LandingPageEditor, {
    formContainerId: 'edit-drawer-content-landing-page',
    isNetworkOwnerAllowed: true,
});

export { LandingPageEditorWithFormDataProvider as LandingPageEditor };
